import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Check, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

const plans = [
  {
    name: "Starter",
    price: "€159",
    period: "/month",
    description: "Perfect for small businesses getting started with AI phone support.",
    features: [
      "Up to 100 calls/month",
      "1 language (NL, FR, or EN)",
      "Basic appointment booking",
      "Business hours handling",
      "Email support",
      "Call transcriptions",
    ],
    cta: "Request Demo",
    popular: false,
  },
  {
    name: "Pro",
    price: "€249",
    period: "/month",
    description: "For growing businesses that need full multilingual support.",
    features: [
      "Up to 500 calls/month",
      "All 3 languages (NL, FR, EN)",
      "Advanced appointment booking",
      "Smart call routing",
      "Call summaries & analytics",
      "Priority email support",
      "Custom greetings",
      "Calendar integrations",
    ],
    cta: "Request Demo",
    popular: true,
  },
  {
    name: "Business",
    price: "€349",
    period: "",
    description: "Enterprise solution with unlimited calls and custom AI training.",
    features: [
      "Unlimited calls",
      "All 3 languages + custom",
      "Custom AI training",
      "Dedicated account manager",
      "Priority phone support",
      "Advanced analytics",
      "API access",
      "SLA guarantee",
      "Custom integrations",
    ],
    cta: "Contact Sales",
    popular: false,
  },
];

const Pricing = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-hero">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">
              Simple, Transparent <span className="text-gradient">Pricing</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Choose the plan that fits your business. No hidden fees, no surprises.
              Start with a 14-day free trial.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 lg:py-28">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`relative p-8 rounded-2xl border transition-all duration-300 ${
                  plan.popular
                    ? "bg-gradient-to-b from-primary/5 to-accent/5 border-primary/30 shadow-card scale-105"
                    : "bg-card border-border hover:border-primary/30 hover:shadow-card"
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="inline-flex items-center px-4 py-1 rounded-full bg-gradient-to-r from-primary to-accent text-primary-foreground text-sm font-medium">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3 className="text-xl font-semibold mb-2">{plan.name}</h3>
                  <div className="flex items-baseline justify-center gap-1 mb-3">
                    <span className="text-4xl font-bold">{plan.price}</span>
                    <span className="text-muted-foreground">{plan.period}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{plan.description}</p>
                </div>

                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  className={`w-full py-6 rounded-xl ${
                    plan.popular ? "" : "variant-outline"
                  }`}
                  variant={plan.popular ? "default" : "outline"}
                  onClick={() => navigate("/contact")}
                >
                  {plan.cta}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            ))}
          </div>

          {/* FAQ Link */}
          <div className="text-center mt-16">
            <p className="text-muted-foreground mb-4">
              Have questions about our pricing?
            </p>
            <Button variant="ghost" onClick={() => navigate("/faq")}>
              Check our FAQ
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Guarantee */}
      <section className="py-16 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl font-bold mb-4">30-Day Money-Back Guarantee</h2>
            <p className="text-muted-foreground">
              Try CallChat risk-free. If you're not satisfied within the first 30 days,
              we'll refund your payment in full. No questions asked.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Pricing;
